import { useSetRecoilState, useRecoilValue } from 'recoil';
import {
    getContourUndoService,
    getStructureSetRepository,
} from '~/domain/ServiceContainer';
import {
    hasUnsavedChangesState,
    patientRepositoryState,
    selectedPatientIdState,
} from '~/state/atoms/patientAtoms';
import { currentStructureSetState } from '~/state/atoms/structureSet/structureSetAtoms';
import { selectedROIState } from '~/state/atoms/structureSet/roiAtoms';
import { selectedToolState } from '~/state/atoms/toolsAtoms';
import {
    primaryImageState,
    secondaryImageState,
} from '~/state/atoms/image/imageAtoms';
import { currentDoseState } from '~/state/atoms/dose/doseAtoms';
import { optimizationObjectivesState } from '~/state/atoms/dose/optimizationObjectivesAtom';
import { exportableItemsCountState } from '~/state/atoms/viewerAtoms';
import { environmentState } from '~/state/atoms/engineAtoms';
import { dicomProcessorState } from '~/state/atoms/dicomProcessorAtoms';
import { closeSnackbar } from 'notistack';
import { jobQueueState } from '~/state/atoms/jobAtoms';

/**
 * Hook that provides a function to fully unload the current patient,
 * clearing all associated state from the viewport, repositories, and atoms.
 *
 * Used when switching to a different patient in clinical mode.
 */
export default function useUnloadPatient() {
    const patientRepository = useRecoilValue(patientRepositoryState);
    const environment = useRecoilValue(environmentState);
    const dicomProcessor = useRecoilValue(dicomProcessorState);

    const setSelectedPatientId = useSetRecoilState(selectedPatientIdState);
    const setHasUnsavedChanges = useSetRecoilState(hasUnsavedChangesState);
    const setPrimaryImage = useSetRecoilState(primaryImageState);
    const setSecondaryImage = useSetRecoilState(secondaryImageState);
    const setCurrentStructureSet = useSetRecoilState(currentStructureSetState);
    const setCurrentDose = useSetRecoilState(currentDoseState);
    const setSelectedROI = useSetRecoilState(selectedROIState);
    const setTool = useSetRecoilState(selectedToolState);
    const setExportableItemsCount = useSetRecoilState(
        exportableItemsCountState
    );
    const setOptimizationObjectives = useSetRecoilState(
        optimizationObjectivesState
    );
    const setJobQueue = useSetRecoilState(jobQueueState);

    /**
     * @param options.preserveDicomProcessor When true, the DicomProcessor's
     *   internal state (parsed patients and processed SOP UIDs) is kept
     *   intact. This is needed when switching patients — the processor
     *   still holds the incoming patient data that must be committed to
     *   the repository after the current patient is unloaded.
     */
    function unloadCurrentPatient(
        options: { preserveDicomProcessor?: boolean } = {}
    ): void {
        // Clear viewport environment
        if (environment) {
            environment.setImage(undefined);
            environment.setSecondaryImage(undefined);
            environment.setStructureSet(undefined);
            environment.setDose(undefined);
        }

        // Clear Recoil UI atoms
        setPrimaryImage(undefined);
        setSecondaryImage(undefined);
        setCurrentStructureSet(undefined);
        setCurrentDose(undefined);
        setSelectedROI(-1);
        setTool('pan');

        // Clear undo history
        const undoService = getContourUndoService();
        undoService.clear();

        // Clear structure set repository ground truths
        const ssRepo = getStructureSetRepository();
        ssRepo.clear();

        // Remove all patients from the in-memory patient repository
        for (const patient of patientRepository.getAll()) {
            patientRepository.delete(patient.id);
        }

        // Clear processed SOP UIDs from the DICOM processor so
        // re-importing the same data later is not blocked.
        // Skipped when switching patients — the processor still holds
        // the incoming patient data that toRepository() needs.
        if (!options.preserveDicomProcessor) {
            dicomProcessor.clearProcessedState();
        }

        // Reset global tracking atoms
        setSelectedPatientId(null);
        setHasUnsavedChanges(false);
        setExportableItemsCount(0);
        setOptimizationObjectives([]);
        setJobQueue((prev) => {
            if (prev.snackbarId) {
                closeSnackbar(prev.snackbarId);
            }
            return { snackbarId: undefined, jobs: [] };
        });
    }

    return { unloadCurrentPatient };
}
